/**
 * Guided Setup Wizard — real 3-step action modal for new signups.
 *
 * Replaces the "Set up your account in 3 minutes" checklist card's role
 * for FIRST-time users who haven't touched anything yet. Unlike the old
 * OnboardingModal (which was a 5-slide carousel with no actions), this
 * one has INLINE forms that actually create the first load, driver, and
 * Gmail connection right inside the modal — the user is done and moving
 * freight before they close the modal.
 *
 * Trigger: mounted on Dashboard. Auto-opens when `onboarding/status`
 * reports `completed < 2 && !wizard_dismissed`. Manually re-openable via
 * a "Guided setup" button on the checklist.
 *
 * Auto-advances a step the moment its API call succeeds — no "Next" click
 * needed on completed steps. Users can also Skip a step (dispatches to
 * the full page for that feature) or Skip All (dismisses).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, MapPin, User as UserIcon, EnvelopeSimple, CheckCircle, ArrowRight,
  Sparkle, ArrowsLeftRight, CurrencyDollar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";

const LS_DISMISS_KEY = "fpai_guided_wizard_dismissed";

export function shouldShowGuidedWizard(status) {
  if (typeof window === "undefined") return false;
  if (localStorage.getItem(LS_DISMISS_KEY)) return false;
  if (!status) return false;
  if (status.dismissed) return false;
  // Show if user has completed 2 or fewer of the tracked steps.
  return (status.completed ?? 0) < 2;
}

const StepChip = ({ n, label, active, done }) => (
  <div
    className={`flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold ${
      done ? "text-emerald-400" : active ? "text-[#FF9764]" : "text-neutral-600"
    }`}
  >
    <div
      className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${
        done
          ? "bg-emerald-400/10 border-emerald-400/40 text-emerald-400"
          : active
          ? "bg-[#FF5A1F]/10 border-[#FF5A1F]/40 text-[#FF9764]"
          : "border-neutral-700 text-neutral-600"
      }`}
    >
      {done ? <CheckCircle size={12} weight="fill" /> : n}
    </div>
    <span className="hidden sm:inline">{label}</span>
  </div>
);

export default function GuidedSetupWizard({ open, onClose, onCompleted }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [stepDone, setStepDone] = useState({ load: false, driver: false, email: false });

  // Load form state
  const [loadForm, setLoadForm] = useState({
    origin: "", destination: "", broker: "", rate: "",
  });
  // Driver form state
  const [driverForm, setDriverForm] = useState({
    full_name: "", phone: "",
  });

  useEffect(() => {
    if (!open) {
      setStep(0);
      setBusy(false);
    }
  }, [open]);

  const dismiss = (permanent = false) => {
    if (permanent) {
      try {
        localStorage.setItem(LS_DISMISS_KEY, "1");
      } catch { /* silent */ }
    }
    onClose?.();
  };

  const advance = () => setStep((s) => Math.min(s + 1, 2));

  const submitLoad = async () => {
    const { origin, destination, broker, rate } = loadForm;
    if (!origin.trim() || !destination.trim()) {
      toast.error("Origin and destination are required");
      return;
    }
    setBusy(true);
    try {
      await client.post("/loads", {
        origin: origin.trim(),
        destination: destination.trim(),
        broker: broker.trim() || null,
        rate: rate ? Number(rate) : null,
        status: "pending",
      });
      toast.success("First load added — nice.");
      setStepDone((s) => ({ ...s, load: true }));
      advance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add load");
    } finally {
      setBusy(false);
    }
  };

  const submitDriver = async () => {
    const { full_name, phone } = driverForm;
    if (!full_name.trim()) {
      toast.error("Driver name is required");
      return;
    }
    setBusy(true);
    try {
      await client.post("/drivers", {
        full_name: full_name.trim(),
        phone: phone.trim() || null,
        status: "available",
      });
      toast.success("Driver added — they'll get SMS updates next.");
      setStepDone((s) => ({ ...s, driver: true }));
      advance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add driver");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
      data-testid="guided-wizard"
    >
      <div className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-2xl overflow-hidden shadow-2xl relative">
        {/* Close (permanent dismiss) */}
        <button
          onClick={() => dismiss(true)}
          data-testid="guided-wizard-close"
          className="absolute top-4 right-4 p-1.5 rounded-md text-neutral-500 hover:text-white hover:bg-white/5 transition-colors"
          title="Close for good"
        >
          <X size={16} />
        </button>

        {/* Header + progress */}
        <div className="px-8 pt-8 pb-5 border-b border-white/5">
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-2">
            <Sparkle size={12} weight="fill" className="inline mr-1" />
            Guided setup
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            You're 3 fields away from your first automated load.
          </h2>
          <div className="flex items-center gap-4 sm:gap-6 mt-5">
            <StepChip n={1} label="First load" active={step === 0} done={stepDone.load} />
            <div className="h-px flex-1 bg-white/10" />
            <StepChip n={2} label="First driver" active={step === 1} done={stepDone.driver} />
            <div className="h-px flex-1 bg-white/10" />
            <StepChip n={3} label="Connect email" active={step === 2} done={stepDone.email} />
          </div>
        </div>

        {/* Step content */}
        <div className="p-8">
          {step === 0 && (
            <div data-testid="wizard-step-load">
              <div className="flex items-center gap-2 text-neutral-400 text-sm mb-4">
                <MapPin size={16} weight="bold" className="text-[#FF9764]" />
                Where's your first load coming from + going to?
              </div>
              <div className="grid gap-3 sm:grid-cols-2 mb-3">
                <input
                  data-testid="wizard-input-origin"
                  value={loadForm.origin}
                  onChange={(e) => setLoadForm({ ...loadForm, origin: e.target.value })}
                  placeholder="Origin (e.g. Dallas, TX)"
                  className="bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
                />
                <input
                  data-testid="wizard-input-destination"
                  value={loadForm.destination}
                  onChange={(e) => setLoadForm({ ...loadForm, destination: e.target.value })}
                  placeholder="Destination (e.g. Atlanta, GA)"
                  className="bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2 mb-6">
                <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[#FF5A1F]/50">
                  <ArrowsLeftRight size={14} className="text-neutral-500 shrink-0" />
                  <input
                    data-testid="wizard-input-broker"
                    value={loadForm.broker}
                    onChange={(e) => setLoadForm({ ...loadForm, broker: e.target.value })}
                    placeholder="Broker name (optional)"
                    className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-neutral-600"
                  />
                </div>
                <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[#FF5A1F]/50">
                  <CurrencyDollar size={14} className="text-neutral-500 shrink-0" />
                  <input
                    data-testid="wizard-input-rate"
                    type="number"
                    value={loadForm.rate}
                    onChange={(e) => setLoadForm({ ...loadForm, rate: e.target.value })}
                    placeholder="Rate (optional)"
                    className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-neutral-600"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => { setStepDone((s) => ({ ...s, load: false })); advance(); }}
                  data-testid="wizard-skip-load"
                  className="text-xs text-neutral-500 hover:text-white transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={submitLoad}
                  disabled={busy}
                  data-testid="wizard-submit-load"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black text-sm font-semibold px-5 py-2.5 disabled:opacity-60 transition-colors"
                >
                  {busy ? "Adding…" : "Add load"} <ArrowRight size={13} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="wizard-step-driver">
              <div className="flex items-center gap-2 text-neutral-400 text-sm mb-4">
                <UserIcon size={16} weight="bold" className="text-[#FF9764]" />
                Add one driver — they'll get SMS updates automatically on every load.
              </div>
              <div className="grid gap-3 sm:grid-cols-2 mb-6">
                <input
                  data-testid="wizard-input-driver-name"
                  value={driverForm.full_name}
                  onChange={(e) => setDriverForm({ ...driverForm, full_name: e.target.value })}
                  placeholder="Driver full name"
                  className="bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
                />
                <input
                  data-testid="wizard-input-driver-phone"
                  value={driverForm.phone}
                  onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
                  placeholder="Cell number (for SMS)"
                  className="bg-black/40 border border-white/10 rounded-lg px-3.5 py-2.5 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => { setStepDone((s) => ({ ...s, driver: false })); advance(); }}
                  data-testid="wizard-skip-driver"
                  className="text-xs text-neutral-500 hover:text-white transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={submitDriver}
                  disabled={busy}
                  data-testid="wizard-submit-driver"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black text-sm font-semibold px-5 py-2.5 disabled:opacity-60 transition-colors"
                >
                  {busy ? "Adding…" : "Add driver"} <ArrowRight size={13} weight="bold" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-email">
              <div className="flex items-center gap-2 text-neutral-400 text-sm mb-4">
                <EnvelopeSimple size={16} weight="bold" className="text-[#FF9764]" />
                Connect Gmail so broker & customer emails go OUT from your address (way higher reply rates than a shared inbox).
              </div>
              <div className="rounded-lg border border-white/10 bg-black/40 p-5 mb-5">
                <div className="text-sm text-neutral-200 leading-relaxed">
                  We'll open the Gmail App-Password setup so you can paste a 16-character app-specific password.
                  Takes 90 seconds and works with any personal or workspace Gmail.
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => dismiss(true)}
                  data-testid="wizard-skip-email"
                  className="text-xs text-neutral-500 hover:text-white transition-colors"
                >
                  Do this later
                </button>
                <Link
                  to="/app/settings/pricing?integration=gmail"
                  onClick={() => {
                    setStepDone((s) => ({ ...s, email: true }));
                    // Fire-and-forget completion callback so parent can refetch
                    onCompleted?.();
                    dismiss(true);
                  }}
                  data-testid="wizard-submit-email"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF5A1F] hover:bg-[#FF7038] text-black text-sm font-semibold px-5 py-2.5 transition-colors"
                >
                  Connect Gmail <ArrowRight size={13} weight="bold" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-white/5 bg-black/30 flex items-center justify-between text-xs">
          <span className="text-neutral-500">Step {step + 1} of 3</span>
          <button
            onClick={() => dismiss(true)}
            data-testid="wizard-skip-all"
            className="text-neutral-500 hover:text-white transition-colors"
          >
            Skip guided setup
          </button>
        </div>
      </div>
    </div>
  );
}
